import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import type { UUID } from '@/index'
import type { FormattedAgentActivity, FormattedUserActivity } from '../utils'
import { useLatestActivity } from '../hooks'
import { ActivityStatusIndicator } from './ActivityStatusIndicator'
import { ActivityStats } from './ActivityStats'
import { ActivityVerb } from './ActivityVerb'
import { ActivityContext } from './ActivityContext'
import { UserActivityContent } from './UserActivityContent'

interface ActivityFeedBarProps {
  readonly sessionId: UUID
  readonly latestActivity?: FormattedAgentActivity | FormattedUserActivity | null
}

export const ActivityFeedBar: FC<ActivityFeedBarProps> = ({ sessionId, latestActivity }) => {
  const { activity } = useLatestActivity({ sessionId, initialActivity: latestActivity })

  if (!activity || activity.status === 'idle') {
    return null
  }

  const isAgentActivity =
    (activity.status === 'pre_tool' || activity.status === 'post_tool') && activity.toolName !== ''
  const isCompacting = activity.activityContext.toLowerCase().includes('compacting')
  const isInterrupt = activity.activityContext.toLowerCase().includes('interrupted')

  return (
    <Box sx={containerSx}>
      <Box sx={iconSlotSx}>
        <ActivityStatusIndicator
          status={activity.status}
          isCompacting={isCompacting}
          isInterrupt={isInterrupt}
        />
      </Box>

      {isAgentActivity ? (
        <>
          <Box sx={slotSx}>
            <ActivityVerb activity={activity} />
          </Box>

          <Box sx={contentSlotSx}>
            <ActivityContext activity={activity} />
          </Box>

          {activity.suffix && (
            <Box sx={slotSx}>
              <ActivityStats activity={activity} />
            </Box>
          )}
        </>
      ) : (
        <Box sx={contentSlotSx}>
          <UserActivityContent activity={activity} />
        </Box>
      )}
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const slotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
}

const iconSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  flexShrink: 0,
}

const contentSlotSx: SxProps<Theme> = {
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
}
